import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCog, Mail, Lock, Loader2, ShieldCheck, ArrowLeft, Smartphone, Copy, Check } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { ROLE_LABEL } from "@/lib/format";

export default function Settings() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();

  // Profile form
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [profilePw, setProfilePw] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  // Password form
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const apkUrl = `${process.env.REACT_APP_BACKEND_URL}/api/app/download`;
  const [copied, setCopied] = useState(false);
  const copyApkLink = async () => {
    try {
      await navigator.clipboard.writeText(apkUrl);
      setCopied(true);
      toast.success("Download link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!profilePw) return toast.error("Enter your current password to confirm the change");
    if (name.trim().length < 2) return toast.error("Name must be at least 2 characters");
    setSavingProfile(true);
    try {
      const { data } = await api.post("/auth/update-profile", {
        current_password: profilePw,
        name: name.trim(),
        email: email.trim().toLowerCase(),
      });
      if (data.unchanged) {
        toast.info("No changes to save");
      } else {
        toast.success("Profile updated");
      }
      setProfilePw("");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update profile");
    } finally { setSavingProfile(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current, new_password: next,
      });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally { setSavingPw(false); }
  };

  const initials = (user?.name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="max-w-3xl mx-auto space-y-5 pb-24">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {/* Account card */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 grid place-items-center text-xl font-bold">
            {initials}
          </div>
          <div>
            <div className="font-display font-bold text-2xl">{user?.name}</div>
            <div className="text-sm text-white/70">{user?.email}</div>
            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
              <ShieldCheck className="w-3 h-3" /> {ROLE_LABEL[user?.role]}
            </span>
          </div>
        </div>
      </div>

      {/* Profile */}
      <form onSubmit={saveProfile} className="rounded-2xl bg-white border border-slate-200 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <UserCog className="w-4 h-4 text-slate-500" />
          <h2 className="font-display font-bold text-lg text-slate-900">Profile</h2>
        </div>
        <p className="text-xs text-slate-500 -mt-2">Change your display name or login email. Re-enter your current password to confirm.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="s-name">Name</Label>
            <Input id="s-name" data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1.5 h-11" required maxLength={80} />
          </div>
          <div>
            <Label htmlFor="s-email">Email</Label>
            <div className="relative mt-1.5">
              <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input id="s-email" type="email" data-testid="settings-email" value={email}
                onChange={(e) => setEmail(e.target.value)} className="h-11 pl-9" required />
            </div>
          </div>
        </div>

        <div>
          <Label htmlFor="s-cpw">Current password</Label>
          <Input id="s-cpw" type="password" data-testid="settings-profile-pw" value={profilePw}
            onChange={(e) => setProfilePw(e.target.value)} className="mt-1.5 h-11" required
            placeholder="Confirm with your current password" />
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={savingProfile} data-testid="settings-profile-save"
            className="h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">
            {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
            Save profile
          </Button>
        </div>
      </form>

      {/* Android App Download */}
      <div data-testid="settings-apk-card" className="rounded-2xl bg-white border border-slate-200 p-6">
        <div className="flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-slate-500" />
          <h2 className="font-display font-bold text-lg text-slate-900">Android App</h2>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Install Job Ticker on your phone or share the link with your team. Android 7.0+ · enable "Install from unknown sources" the first time.
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-[auto_1fr] items-center">
          <div className="bg-white p-2 rounded-lg border border-slate-100 justify-self-start sm:justify-self-center">
            <QRCodeSVG value={apkUrl} size={120} level="M" includeMargin={false} />
          </div>
          <div className="space-y-3 min-w-0">
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={apkUrl}
                data-testid="settings-apk-url"
                className="h-10 text-xs text-slate-600 bg-slate-50 truncate"
                onFocus={(e) => e.target.select()}
              />
              <Button
                type="button"
                variant="outline"
                onClick={copyApkLink}
                data-testid="settings-apk-copy"
                className="h-10 shrink-0"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <a
              href={apkUrl}
              data-testid="settings-apk-download"
              className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors"
            >
              <Smartphone className="w-4 h-4" />
              Download .apk
            </a>
          </div>
        </div>
      </div>

      {/* Password */}
      <form onSubmit={savePassword} className="rounded-2xl bg-white border border-slate-200 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-slate-500" />
          <h2 className="font-display font-bold text-lg text-slate-900">Password</h2>
        </div>
        <p className="text-xs text-slate-500 -mt-2">Use a fresh, strong password. Minimum 6 characters.</p>

        <div>
          <Label htmlFor="s-pw-cur">Current password</Label>
          <Input id="s-pw-cur" type="password" data-testid="settings-pw-current" value={current}
            onChange={(e) => setCurrent(e.target.value)} className="mt-1.5 h-11" required />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="s-pw-new">New password</Label>
            <Input id="s-pw-new" type="password" data-testid="settings-pw-new" value={next}
              onChange={(e) => setNext(e.target.value)} className="mt-1.5 h-11" required />
          </div>
          <div>
            <Label htmlFor="s-pw-conf">Confirm new password</Label>
            <Input id="s-pw-conf" type="password" data-testid="settings-pw-confirm" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-11" required />
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={savingPw} data-testid="settings-pw-save"
            className="h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">
            {savingPw ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
            Update password
          </Button>
        </div>
      </form>
    </div>
  );
}
